import React, { memo } from 'react';
import { View, Platform, Text as RnText } from 'react-native';
import { CardView, ProgressBar, rem, Spacer } from 'design-system';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { formatAmount, interpolate } from 'utils';
import createStyleSheets from 'utils/createStyleSheets';
import { useAppSelector } from 'store/hooks';
import { isDefaultCategory, useCategories } from 'hooks/useCategory';
import IconCircle from 'design-system/icons/IconCircle';
import IconTransactionCategory from 'features/transactions/icons/IconTransactionCategory';
import { selectDefaultCurrency } from 'reducers/selectors';
import { isWeb } from '../../../constants';
const mockData = {
    budgetCategories: [
        {
            total: -980,
            totalWithExcluded: -980,
            currency: 'USD',
            transactionsCount: 0,
            id: 'bills',
            displayName: 'Bills',
            color: 'rgb(48,53,57)',
            emoji: '🧾',
            iconUrl: 'https://storage.googleapis.com/emma-app.com/images/icons/categories/bills.png',
            whiteBgIconUrl: 'https://app.emma-cdn.com/images/icons/categories_v2/bills.png',
            budgetLimit: 1400,
            baseLimit: 1200,
            rollingAccumulatedLimit: null,
            totalLimit: 1200,
        },
        {
            total: -150,
            totalWithExcluded: -150,
            currency: 'USD',
            transactionsCount: 0,
            id: 'eatingout',
            displayName: 'Eating Out',
            color: 'rgb(0,204,204)',
            emoji: '🌯',
            iconUrl: 'https://storage.googleapis.com/emma-app.com/images/icons/categories/eatingout.png',
            whiteBgIconUrl: 'https://app.emma-cdn.com/images/icons/categories_v2/eatingout.png',
            budgetLimit: 320,
            baseLimit: 300,
            rollingAccumulatedLimit: null,
            totalLimit: 300,
        },
        {
            total: -160,
            totalWithExcluded: -160,
            currency: 'USD',
            transactionsCount: 0,
            id: 'education',
            displayName: 'Education',
            color: 'rgb(254,96,133)',
            emoji: '📚',
            iconUrl: 'https://storage.googleapis.com/emma-app.com/images/icons/categories/education.png',
            whiteBgIconUrl: 'https://app.emma-cdn.com/images/icons/categories_v2/education.png',
            budgetLimit: 200,
            baseLimit: 200,
            rollingAccumulatedLimit: null,
            totalLimit: 300,
        },
    ],
    allocatedBudget: {
        totalBudget: 2400,
        totalAllocated: 1920,
    },
};
const SetUpBudgetsContentHeader = () => {
    const currency = useAppSelector(selectDefaultCurrency);
    const colors = useColors();
    const styles = useStyles(styleSet);
    const categories = useCategories();
    const allocationCard = (totalBudget, totalAllocated) => {
        const percentAllocated = (totalAllocated / totalBudget) * 100;
        const amountRemaining = totalBudget - totalAllocated;
        const progressBarColor = amountRemaining < 0
            ? [colors.elements.negative, colors.elements.negative]
            : [colors.elements.brand, colors.elements.brand];
        return (<View style={styles.cardContainer}>
        <View style={[styles.containerAllocationCard]}>
          <View style={[styles.titleRow, { justifyContent: 'space-between' }]}>
            <RnText style={[styles.numbers_14, { color: colors.text.primary }]}>
              {Math.round(percentAllocated)}% set
            </RnText>

            <RnText style={[styles.numbers_14, { color: colors.text.primary }]}>
              {formatAmount(totalAllocated, currency, 2)}/
              {formatAmount(totalBudget, currency, 2)}
            </RnText>
          </View>
          <Spacer h={6}/>
          <ProgressBar position={percentAllocated || 0} animateProgress={false} barBackgroundColor={colors.background.dark} colors={progressBarColor}/>
          <Spacer h={6}/>
          <RnText style={[styles.text_thin_12, { color: colors.text.secondary }]}>
            <RnText style={[styles.text_thin_12, { color: colors.text.secondary }]}>
              {formatAmount(amountRemaining, currency, 2)}
            </RnText>{' '}
            left to allocate
          </RnText>
        </View>
      </View>);
    };
    const categoryRow = (category, key) => {
        const positive = category.total > 0;
        const leftOfBudget = (category.budgetLimit || 0) - Math.abs(category.total);
        const percentage = interpolate(positive ? 0 : Math.abs(category.total), {
            inputRange: [0, category.budgetLimit || 0],
            outputRange: [0, 100],
        });
        const barColor = () => {
            if (isDefaultCategory(category.id)) {
                const { color } = categories[category.id];
                return [color, color];
            }
            return [
                colors.categories.custom,
                colors.categories.custom,
            ];
        };
        return (<View style={styles.containerRow} key={key}>
        <View style={styles.content}>
          <IconCircle width={40} height={40}>
            <IconTransactionCategory category={category}/>
          </IconCircle>
          <View style={styles.innerContent}>
            <>
              <View style={styles.titleContainer}>
                <RnText style={[
                styles.displayName,
                styles.text_14,
                { color: colors.text.primary },
            ]}>
                  {category.displayName}
                </RnText>
                <RnText style={[styles.numbers_14, { color: colors.text.primary }]}>
                  {formatAmount(category.total, currency, 2)}
                </RnText>
              </View>
              <>
                <RnText style={[
                styles.subTitle,
                styles.text_thin_12,
                { color: colors.text.secondary },
            ]}>
                  {formatAmount(positive ? category.budgetLimit : Math.abs(leftOfBudget), currency, 2)}
                  {' left of '}
                  {formatAmount(category.budgetLimit, currency, 2)}
                </RnText>
                <ProgressBar position={percentage} colors={barColor()}/>
              </>
            </>
          </View>
        </View>
      </View>);
    };
    return (<View style={styles.container}>
      <View style={styles.cardsContainer}>
        <View style={[
            styles.allocationCardContainer,
            isWeb || Platform.OS === 'android' ? null : styles.testShadow,
        ]}>
          <CardView noMarginTop noMarginBottom webPadding style={[
            isWeb || Platform.OS === 'android' ? styles.testShadow : null,
            Platform.OS === 'android' ? styles.androidExtraStyle : null,
        ]}>
            {allocationCard(mockData.allocatedBudget.totalBudget, mockData.allocatedBudget.totalAllocated)}
          </CardView>
        </View>
        <View style={styles.categoriesCardContainer}>
          <CardView noMarginTop noMarginBottom webPadding>
            {mockData.budgetCategories.map((category) => categoryRow(category, category.id))}
          </CardView>
        </View>
      </View>
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        marginHorizontal: rem(isWeb ? 16 : -16),
        marginTop: rem(20),
    },
    cardsContainer: {
        paddingHorizontal: rem(22),
    },
    allocationCardContainer: {
        zIndex: 1,
        transform: [{ translateY: rem(10) }, { translateX: rem(12) }],
    },
    categoriesCardContainer: {
        transform: [{ translateX: rem(-8) }],
    },
    testShadow: {
        ...Platform.select({
            ios: {
                shadowColor: '#133068',
                shadowOffset: { width: 5, height: 10 },
                shadowOpacity: 0.07,
                shadowRadius: 10,
            },
            android: {
                elevation: 20,
                shadowOpacity: 0.2,
            },
            default: {
                shadowColor: '#133068',
                shadowOffset: { width: 5, height: 10 },
                shadowOpacity: 0.07,
                shadowRadius: 10,
            },
        }),
    },
    androidExtraStyle: {
        borderRadius: rem(16),
        overflow: 'hidden',
    },
    containerAllocationCard: {
        padding: rem(16),
        backgroundColor: colors.cards.onDark,
    },
    cardContainer: {},
    titleRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    text_14: {
        fontFamily: 'Montserrat-Regular',
        fontSize: rem(14),
        lineHeight: rem(18),
    },
    text_thin_12: {
        fontFamily: 'Montserrat-Light',
        fontSize: rem(12),
        lineHeight: rem(16),
    },
    numbers_14: {
        fontFamily: 'Nunito-Bold',
        fontSize: rem(14),
        lineHeight: rem(18),
    },
    containerRow: {
        padding: rem(14),
        justifyContent: 'center',
    },
    content: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
    },
    innerContent: {
        flex: 1,
        marginStart: rem(14),
        flexDirection: 'column',
    },
    titleContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
    },
    displayName: {
        marginBottom: rem(4),
        flexShrink: 1,
        paddingEnd: rem(12),
    },
    subTitle: {
        marginBottom: rem(8),
    },
}));
export default memo(SetUpBudgetsContentHeader);
